import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  LinearProgress,
  Rating,
  Fab,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  styled,
  alpha,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { GridLegacy as Grid } from '@mui/material';
import {
  Star,
  AutoAwesome,
  Psychology,
  Favorite,
  TrendingUp,
  Schedule,
  Share,
  Download,
  Print,
  ExpandMore,
  ThumbUp,
  Comment,
  Refresh,
  MoreVert,
  PlayArrow,
  Pause,
  BookmarkBorder,
  BookmarkAdded
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Types
interface ReadingSection {
  id: string;
  title: string;
  content: string;
  category: 'overview' | 'love' | 'career' | 'health' | 'finance' | 'spiritual';
  confidence: number;
  keyPoints: string[];
  timeframe?: string;
  advice?: string;
}

interface ReportData {
  id: string;
  title: string;
  type: 'daily' | 'weekly' | 'monthly' | 'transit' | 'compatibility' | 'natal';
  generatedDate: Date;
  targetDate: string;
  userInfo: {
    name: string;
    birthSign: string;
    moonSign: string;
    risingSign: string;
  };
  sections: ReadingSection[];
  overallScore: number;
  tags: string[];
  estimatedReadTime: number;
  audioAvailable: boolean;
}

// Custom theme colors (using your brand palette)
const brandColors = {
  primary: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#1E40AF',
  },
  secondary: {
    main: '#8B5CF6',
    light: '#A78BFA',
    dark: '#6D28D9',
  },
  accent: {
    main: '#F97316',
    light: '#FB923C',
    dark: '#EA580C',
  }
};

// Styled Components
const ReportContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: `linear-gradient(135deg, 
    #030712 0%, 
    ${alpha(brandColors.primary.dark, 0.1)} 25%,
    ${alpha(brandColors.secondary.dark, 0.05)} 75%,
    #030712 100%)`,
  position: 'relative',
  overflow: 'hidden',
}));

const ReportHeader = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha('#1E293B', 0.8)} 0%, 
    ${alpha(brandColors.primary.main, 0.1)} 100%)`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
  borderRadius: '24px',
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const SectionCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha('#1E293B', 0.8)} 0%, 
    ${alpha(brandColors.secondary.main, 0.05)} 100%)`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(brandColors.secondary.main, 0.2)}`,
  borderRadius: '20px',
  marginBottom: theme.spacing(3),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: alpha(brandColors.secondary.main, 0.4),
    boxShadow: `0 20px 40px ${alpha(brandColors.secondary.main, 0.2)}`,
  },
}));

const CategoryIcon = styled(Box)<{ category: string }>(({ theme, category }) => {
  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case 'love': return '#EC4899';
      case 'career': return '#10B981';
      case 'health': return '#F59E0B';
      case 'finance': return '#8B5CF6';
      case 'spiritual': return '#A855F7';
      default: return brandColors.primary.main;
    }
  };

  return {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${getCategoryColor(category)}, ${alpha(getCategoryColor(category), 0.7)})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    boxShadow: `0 8px 24px ${alpha(getCategoryColor(category), 0.3)}`,
  };
});

const ConfidenceBar = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 6,
  backgroundColor: alpha('#1E293B', 0.6),
  borderRadius: 3,
  overflow: 'hidden',
  marginTop: theme.spacing(1),
}));

const ActionFab = styled(Fab)(({ theme }) => ({
  background: `linear-gradient(135deg, ${brandColors.accent.main}, ${brandColors.accent.dark})`,
  color: 'white',
  '&:hover': {
    background: `linear-gradient(135deg, ${brandColors.accent.light}, ${brandColors.accent.main})`,
    transform: 'scale(1.1)',
  },
  transition: 'all 0.3s ease',
}));

const ReadingReportDisplay: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string>('overview');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [userComment, setUserComment] = useState('');
  const [userRating, setUserRating] = useState<number | null>(null);

  console.log(isMobile);
  // Helper function to safely extract key points arrays
  const getKeyPoints = (sectionKey: string): string[] => {
    const keyPoints = t(`readingReport.sections.${sectionKey}.keyPoints`);
    if (Array.isArray(keyPoints)) {
      // Convert MessageFormatElement[] to string[] by extracting the value property
      return keyPoints.map((item: any) => typeof item === 'string' ? item : item.value || '');
    }
    return [];
  };

  // Mock report data
  const reportData: ReportData = {
    id: 'daily-2025-06-28',
    title: t('readingReport.title'),
    type: 'daily',
    generatedDate: new Date(),
    targetDate: 'June 28, 2025',
    userInfo: {
      name: 'Sarah Chen',
      birthSign: 'Gemini',
      moonSign: 'Scorpio',
      risingSign: 'Leo'
    },
    sections: [
      {
        id: 'overview',
        title: t('readingReport.sections.overview.title'),
        content: t('readingReport.sections.overview.content'),
        category: 'overview',
        confidence: 92,
        keyPoints: getKeyPoints('overview'),
        timeframe: t('readingReport.sections.overview.timeframe'),
        advice: t('readingReport.sections.overview.advice')
      },
      {
        id: 'love',
        title: t('readingReport.sections.love.title'),
        content: t('readingReport.sections.love.content'),
        category: 'love',
        confidence: 88,
        keyPoints: getKeyPoints('love'),
        timeframe: t('readingReport.sections.love.timeframe'),
        advice: t('readingReport.sections.love.advice')
      },
      {
        id: 'career',
        title: t('readingReport.sections.career.title'),
        content: t('readingReport.sections.career.content'),
        category: 'career',
        confidence: 85,
        keyPoints: getKeyPoints('career'),
        timeframe: t('readingReport.sections.career.timeframe'),
        advice: t('readingReport.sections.career.advice')
      },
      {
        id: 'health',
        title: t('readingReport.sections.health.title'),
        content: t('readingReport.sections.health.content'),
        category: 'health',
        confidence: 78,
        keyPoints: getKeyPoints('health'),
        timeframe: t('readingReport.sections.health.timeframe'),
        advice: t('readingReport.sections.health.advice')
      },
      {
        id: 'finance',
        title: t('readingReport.sections.finance.title'),
        content: t('readingReport.sections.finance.content'),
        category: 'finance',
        confidence: 82,
        keyPoints: getKeyPoints('finance'),
        timeframe: t('readingReport.sections.finance.timeframe'),
        advice: t('readingReport.sections.finance.advice')
      },
      {
        id: 'spiritual',
        title: t('readingReport.sections.spiritual.title'),
        content: t('readingReport.sections.spiritual.content'),
        category: 'spiritual',
        confidence: 90,
        keyPoints: getKeyPoints('spiritual'),
        timeframe: t('readingReport.sections.spiritual.timeframe'),
        advice: t('readingReport.sections.spiritual.advice')
      }
    ],
    overallScore: 87,
    tags: ['Communication', 'Intuition', 'Relationships', 'Growth', 'Transformation'],
    estimatedReadTime: 8,
    audioAvailable: true
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'love': return <Favorite sx={{ color: 'white', fontSize: 24 }} />;
      case 'career': return <TrendingUp sx={{ color: 'white', fontSize: 24 }} />;
      case 'health': return <Psychology sx={{ color: 'white', fontSize: 24 }} />;
      case 'finance': return <Star sx={{ color: 'white', fontSize: 24 }} />;
      case 'spiritual': return <AutoAwesome sx={{ color: 'white', fontSize: 24 }} />;
      default: return <Star sx={{ color: 'white', fontSize: 24 }} />;
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 85) return '#10B981';
    if (confidence >= 70) return '#F59E0B';
    return '#EF4444';
  };

  const handleToggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const handleToggleAudio = () => {
    setIsPlaying(!isPlaying);
    // In real app, this would control text-to-speech
  };

  const handleSectionToggle = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? '' : sectionId);
  };

  const handleShare = () => {
    setShareDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleComment = () => {
    setCommentDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleSubmitComment = () => {
    // In real app, this would submit to API
    console.log('Comment submitted:', userComment, 'Rating:', userRating);
    setCommentDialogOpen(false);
    setUserComment('');
    setUserRating(null);
  };

  return (
    <ReportContainer>
      {/* Animated Background */}
      <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {Array.from({ length: 25 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `twinkle ${2 + Math.random() * 3}s infinite`,
              animationDelay: `${Math.random() * 3}s`,
              '@keyframes twinkle': {
                '0%, 100%': { opacity: 0.3 },
                '50%': { opacity: 0.8 },
              },
            }}
          >
            <Star sx={{ fontSize: 8, color: 'white' }} />
          </Box>
        ))}
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 4 }}>
        {/* Report Header */}
        <ReportHeader>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ 
                  background: `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
                  width: 56,
                  height: 56
                }}>
                  <AutoAwesome sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                    {reportData.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                    {t('readingReport.forUser', { name: reportData.userInfo.name, date: reportData.targetDate })}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                <Chip
                  label={`${reportData.userInfo.birthSign} ☉`}
                  sx={{
                    background: alpha('#FF6B6B', 0.2),
                    color: '#FF6B6B',
                    fontWeight: 600
                  }}
                />
                <Chip
                  label={`${reportData.userInfo.moonSign} ☽`}
                  sx={{
                    background: alpha('#96CEB4', 0.2),
                    color: '#96CEB4',
                    fontWeight: 600
                  }}
                />
                <Chip
                  label={`${reportData.userInfo.risingSign} ↗`}
                  sx={{
                    background: alpha('#FFEAA7', 0.2),
                    color: '#FFEAA7',
                    fontWeight: 600
                  }}
                />
                <Typography variant="body2" sx={{ color: '#64748B' }}>
                  • {t('readingReport.minRead', { minutes: reportData.estimatedReadTime })}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                  {t('readingReport.overallAlignment')}
                </Typography>
                <Typography variant="h6" sx={{ color: getConfidenceColor(reportData.overallScore), fontWeight: 700 }}>
                  {reportData.overallScore}%
                </Typography>
              </Box>
              
              <LinearProgress
                variant="determinate"
                value={reportData.overallScore}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: alpha('#1E293B', 0.6),
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getConfidenceColor(reportData.overallScore),
                    borderRadius: 4,
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
              <Tooltip title={isBookmarked ? t('readingReport.removeBookmarkTooltip') : t('readingReport.bookmarkTooltip')}>
                <IconButton
                  onClick={handleToggleBookmark}
                  sx={{
                    color: isBookmarked ? '#F59E0B' : '#64748B',
                    '&:hover': { color: '#F59E0B' }
                  }}
                >
                  {isBookmarked ? <BookmarkAdded /> : <BookmarkBorder />}
                </IconButton>
              </Tooltip>
              
              {reportData.audioAvailable && (
                <Tooltip title={isPlaying ? t('readingReport.pauseAudioTooltip') : t('readingReport.playAudioTooltip')}>
                  <IconButton
                    onClick={handleToggleAudio}
                    sx={{
                      color: isPlaying ? brandColors.accent.main : '#64748B',
                      '&:hover': { color: brandColors.accent.main }
                    }}
                  >
                    {isPlaying ? <Pause /> : <PlayArrow />}
                  </IconButton>
                </Tooltip>
              )}
              
              <Tooltip title={t('readingReport.moreActionsTooltip')}>
                <IconButton
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={{ color: '#64748B' }}
                >
                  <MoreVert />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Tags */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {reportData.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                sx={{
                  background: alpha(brandColors.secondary.main, 0.2),
                  color: brandColors.secondary.light,
                  border: `1px solid ${alpha(brandColors.secondary.main, 0.3)}`
                }}
              />
            ))}
          </Box>
        </ReportHeader>

        {/* Report Sections */}
        <Box>
          {reportData.sections.map((section) => (
            <SectionCard key={section.id}>
              <Accordion
                expanded={expandedSection === section.id}
                onChange={() => handleSectionToggle(section.id)}
                sx={{
                  background: 'transparent',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '& .MuiAccordionSummary-root': {
                    padding: theme.spacing(3),
                    '&:hover': {
                      background: alpha(brandColors.secondary.main, 0.05)
                    }
                  },
                  '& .MuiAccordionDetails-root': {
                    padding: theme.spacing(0, 3, 3, 3)
                  }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white' }} />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 3 }}>
                    <CategoryIcon category={section.category}>
                      {getCategoryIcon(section.category)}
                    </CategoryIcon>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 1 }}>
                        {section.title}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                          {t('readingReport.confidence', { percentage: section.confidence })}
                        </Typography>
                        {section.timeframe && (
                          <>
                            <Typography variant="body2" sx={{ color: '#64748B' }}>•</Typography>
                            <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                              {section.timeframe}
                            </Typography>
                          </>
                        )}
                      </Box>
                      
                      <ConfidenceBar>
                        <Box
                          sx={{
                            width: `${section.confidence}%`,
                            height: '100%',
                            background: getConfidenceColor(section.confidence),
                            borderRadius: 3,
                            transition: 'width 0.5s ease'
                          }}
                        />
                      </ConfidenceBar>
                    </Box>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails>
                  <Box sx={{ pl: 9 }}>
                    <Typography variant="body1" sx={{ color: '#E2E8F0', lineHeight: 1.7, mb: 3 }}>
                      {section.content}
                    </Typography>
                    
                    {section.keyPoints.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 2 }}>
                          {t('readingReport.keyInsights')}
                        </Typography>
                        <List sx={{ py: 0 }}>
                          {section.keyPoints.map((point, index) => (
                            <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Star sx={{ fontSize: 16, color: brandColors.accent.main }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={point}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  sx: { color: '#CBD5E1' }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                    
                    {section.advice && (
                      <Box sx={{
                        background: alpha(brandColors.primary.main, 0.1),
                        border: `1px solid ${alpha(brandColors.primary.main, 0.3)}`,
                        borderRadius: 2,
                        p: 3
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: brandColors.primary.light, mb: 1 }}>
                          {t('readingReport.cosmicAdvice')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#CBD5E1', fontStyle: 'italic' }}>
                          {section.advice}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </SectionCard>
          ))}
        </Box>

        {/* Floating Action Buttons */}
        <Box sx={{ position: 'fixed', bottom: 24, right: 24, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Tooltip title={t('readingReport.shareReport')} placement="left">
            <ActionFab
              size="medium"
              onClick={handleShare}
            >
              <Share />
            </ActionFab>
          </Tooltip>
          
          <Tooltip title={t('readingReport.rateReview')} placement="left">
            <ActionFab
              size="medium"
              onClick={handleComment}
            >
              <Comment />
            </ActionFab>
          </Tooltip>
        </Box>

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          PaperProps={{
            sx: {
              background: `linear-gradient(135deg, ${alpha('#1E293B', 0.95)}, ${alpha(brandColors.primary.main, 0.1)})`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
              borderRadius: '12px',
              color: 'white'
            }
          }}
        >
          <MenuItem onClick={handleShare}>
            <Share sx={{ mr: 2 }} />
            {t('readingReport.shareReport')}
          </MenuItem>
          <MenuItem onClick={() => setMenuAnchor(null)}>
            <Download sx={{ mr: 2 }} />
            {t('readingReport.downloadPdf')}
          </MenuItem>
          <MenuItem onClick={() => setMenuAnchor(null)}>
            <Print sx={{ mr: 2 }} />
            {t('readingReport.printReport')}
          </MenuItem>
          <Divider sx={{ borderColor: alpha(brandColors.primary.main, 0.2) }} />
          <MenuItem onClick={handleComment}>
            <ThumbUp sx={{ mr: 2 }} />
            {t('readingReport.rateReview')}
          </MenuItem>
          <MenuItem onClick={() => setMenuAnchor(null)}>
            <Refresh sx={{ mr: 2 }} />
            {t('readingReport.generateNew')}
          </MenuItem>
        </Menu>

        {/* Share Dialog */}
        <Dialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: `linear-gradient(135deg, ${alpha('#1E293B', 0.95)}, ${alpha(brandColors.primary.main, 0.1)})`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
              borderRadius: '20px',
              color: 'white'
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Share sx={{ fontSize: 32, color: brandColors.primary.main, mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {t('readingReport.shareTitle')}
            </Typography>
          </DialogTitle>
          
          <DialogContent>
            <Typography variant="body1" sx={{ color: '#94A3B8', mb: 3, textAlign: 'center' }}>
              {t('readingReport.shareDescription')}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{
                    borderColor: '#1DA1F2',
                    color: '#1DA1F2',
                    '&:hover': {
                      backgroundColor: alpha('#1DA1F2', 0.1),
                      borderColor: '#1DA1F2'
                    }
                  }}
                >
                  {t('readingReport.twitter')}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{
                    borderColor: '#1877F2',
                    color: '#1877F2',
                    '&:hover': {
                      backgroundColor: alpha('#1877F2', 0.1),
                      borderColor: '#1877F2'
                    }
                  }}
                >
                  {t('readingReport.facebook')}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{
                    borderColor: '#25D366',
                    color: '#25D366',
                    '&:hover': {
                      backgroundColor: alpha('#25D366', 0.1),
                      borderColor: '#25D366'
                    }
                  }}
                >
                  {t('readingReport.whatsapp')}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{
                    borderColor: brandColors.primary.main,
                    color: brandColors.primary.main,
                    '&:hover': {
                      backgroundColor: alpha(brandColors.primary.main, 0.1),
                      borderColor: brandColors.primary.main
                    }
                  }}
                >
                  {t('readingReport.copyLink')}
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button 
              onClick={() => setShareDialogOpen(false)}
              sx={{ color: '#94A3B8' }}
            >
              {t('readingReport.cancel')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Comment/Rating Dialog */}
        <Dialog
          open={commentDialogOpen}
          onClose={() => setCommentDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: `linear-gradient(135deg, ${alpha('#1E293B', 0.95)}, ${alpha(brandColors.secondary.main, 0.1)})`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(brandColors.secondary.main, 0.2)}`,
              borderRadius: '20px',
              color: 'white'
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <ThumbUp sx={{ fontSize: 32, color: brandColors.secondary.main, mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {t('readingReport.rateTitle')}
            </Typography>
          </DialogTitle>
          
          <DialogContent>
            <Typography variant="body1" sx={{ color: '#94A3B8', mb: 3, textAlign: 'center' }}>
              {t('readingReport.rateDescription')}
            </Typography>
            
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="body2" sx={{ color: 'white', mb: 2 }}>
                {t('readingReport.overallRating')}
              </Typography>
              <Rating
                value={userRating}
                onChange={(event, newValue) => setUserRating(newValue)}
                size="large"
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#F59E0B',
                  },
                  '& .MuiRating-iconHover': {
                    color: '#FBBF24',
                  },
                }}
              />
            </Box>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              value={userComment}
              onChange={(e) => setUserComment(e.target.value)}
              placeholder={t('readingReport.commentPlaceholder')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: alpha('#0F172A', 0.6),
                  borderRadius: '12px',
                  '& fieldset': { 
                    borderColor: alpha(brandColors.secondary.main, 0.3),
                  },
                  '&:hover fieldset': { 
                    borderColor: brandColors.secondary.main 
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: brandColors.secondary.light 
                  },
                },
                '& .MuiInputBase-input': { 
                  color: 'white',
                  '&::placeholder': { 
                    color: '#94A3B8' 
                  }
                },
              }}
            />
          </DialogContent>
          
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button 
              onClick={() => setCommentDialogOpen(false)}
              sx={{ color: '#94A3B8' }}
            >
              {t('readingReport.cancel')}
            </Button>
            <Button
              onClick={handleSubmitComment}
              disabled={!userRating}
              variant="contained"
              sx={{
                background: `linear-gradient(135deg, ${brandColors.secondary.main}, ${brandColors.secondary.dark})`,
                borderRadius: '12px',
                px: 3,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  background: `linear-gradient(135deg, ${brandColors.secondary.light}, ${brandColors.secondary.main})`,
                },
                '&:disabled': {
                  background: alpha('#64748B', 0.3),
                  color: '#94A3B8'
                }
              }}
            >
              {t('readingReport.submitFeedback')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bottom Summary Card */}
        <Box sx={{ mt: 6, mb: 4 }}>
          <Paper sx={{
            background: `linear-gradient(135deg, 
              ${alpha('#1E293B', 0.8)} 0%, 
              ${alpha(brandColors.accent.main, 0.1)} 100%)`,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(brandColors.accent.main, 0.3)}`,
            borderRadius: '24px',
            p: 4
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'white', mb: 3, textAlign: 'center' }}>
              {t('readingReport.actionPlanTitle')}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, #10B981, #059669)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px auto'
                  }}>
                    <TrendingUp sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 1 }}>
                    {t('readingReport.focusAreas')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    {t('readingReport.focusAreasDescription')}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, #F59E0B, #D97706)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px auto'
                  }}>
                    <Schedule sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 1 }}>
                    {t('readingReport.optimalTiming')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    {t('readingReport.optimalTimingDescription')}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${brandColors.secondary.main}, ${brandColors.secondary.dark})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px auto'
                  }}>
                    <AutoAwesome sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 1 }}>
                    {t('readingReport.keyAffirmation')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#CBD5E1', fontStyle: 'italic' }}>
                    {t('readingReport.keyAffirmationText')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Related Readings */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'white', mb: 3 }}>
            {t('readingReport.relatedReadings')}
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{
                background: `linear-gradient(135deg, 
                  ${alpha('#1E293B', 0.8)} 0%, 
                  ${alpha(brandColors.primary.main, 0.1)} 100%)`,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
                borderRadius: '16px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  borderColor: brandColors.primary.main,
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 1 }}>
                    {t('readingReport.weeklyForecast')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94A3B8', mb: 2 }}>
                    {t('readingReport.weeklyForecastDate')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    {t('readingReport.weeklyForecastDescription')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{
                background: `linear-gradient(135deg, 
                  ${alpha('#1E293B', 0.8)} 0%, 
                  ${alpha('#EC4899', 0.1)} 100%)`,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha('#EC4899', 0.2)}`,
                borderRadius: '16px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  borderColor: '#EC4899',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 1 }}>
                    {t('readingReport.loveCompatibility')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94A3B8', mb: 2 }}>
                    {t('readingReport.loveCompatibilitySigns')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    {t('readingReport.loveCompatibilityDescription')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{
                background: `linear-gradient(135deg, 
                  ${alpha('#1E293B', 0.8)} 0%, 
                  ${alpha('#10B981', 0.1)} 100%)`,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha('#10B981', 0.2)}`,
                borderRadius: '16px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  borderColor: '#10B981',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 1 }}>
                    {t('readingReport.transitReport')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94A3B8', mb: 2 }}>
                    {t('readingReport.transitReportSubtitle')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    {t('readingReport.transitReportDescription')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </ReportContainer>
  );
};

export default ReadingReportDisplay;